import { api } from '@smena/ui';

export interface AccountCounters {
  properties: number;
  bookings: number;
  sessions: number;
  escalationsOpen: number;
  staffUsers: number;
}

export interface AccountHealth {
  channelsError: number;
  icalError: number;
  avitoStatus: string | null;
}

export interface AccountPlan {
  base_rub?: number;
  per_property_rub?: number;
  alpha?: boolean;
  last_paid_at?: string;
}

export interface AccountListItem {
  id: string;
  slug: string;
  name: string;
  kind: 'client' | 'demo';
  plan: AccountPlan;
  setup: { completedAt: string | null; currentStep: number } | null;
  counters: AccountCounters;
  health: AccountHealth;
  createdAt: string;
}

export interface AccountStaffUser {
  id: string;
  email: string;
  name: string;
  role: string;
  active: boolean;
  lastLoginAt: string | null;
}

export interface AccountCard {
  id: string;
  slug: string;
  name: string;
  kind: 'client' | 'demo';
  plan: AccountPlan;
  settings: Record<string, unknown>;
  tgOperatorChatId: number | null;
  createdAt: string;
  counters: AccountCounters;
  channels: Array<{
    channel: string;
    kind: string;
    status: string;
    botUsername: string | null;
    lastError: string | null;
  }>;
  avito: {
    authKind: string;
    status: string;
    tariffOk: boolean;
    lastSyncAt: string | null;
    lastError: string | null;
  } | null;
  icalFeeds: Array<{
    id: string;
    label: string | null;
    active: boolean;
    lastPolledAt: string | null;
    lastStatus: string | null;
    lastError: string | null;
    lastEventsCount: number | null;
  }>;
  staffUsers: AccountStaffUser[];
}

export async function fetchAccounts(): Promise<AccountListItem[]> {
  const r = await api<{ items: AccountListItem[] }>('/api/admin/accounts');
  return r.items;
}

export function fetchAccount(id: string): Promise<AccountCard> {
  return api<AccountCard>(`/api/admin/accounts/${id}`);
}

export function createAccount(input: {
  slug: string;
  name: string;
  kind: 'client' | 'demo';
  ownerEmail: string;
  ownerName: string;
}): Promise<{ account: AccountCard; owner: { email: string; password: string } }> {
  return api('/api/admin/accounts', { method: 'POST', json: input });
}

export function patchPlan(id: string, plan: AccountPlan): Promise<{ plan: AccountPlan }> {
  return api(`/api/admin/accounts/${id}/plan`, { method: 'PATCH', json: plan });
}

export function patchSettings(
  id: string,
  patch: Record<string, boolean>,
): Promise<{ settings: Record<string, unknown> }> {
  return api(`/api/admin/accounts/${id}/settings`, { method: 'PATCH', json: patch });
}

export function patchKind(id: string, kind: 'client' | 'demo'): Promise<{ kind: string }> {
  return api(`/api/admin/accounts/${id}/kind`, { method: 'PATCH', json: { kind } });
}

export function resetStaffPassword(
  id: string,
  uid: string,
): Promise<{ email: string; password: string }> {
  return api(`/api/admin/accounts/${id}/staff-users/${uid}/reset-password`, { method: 'POST' });
}

export function setStaffActive(id: string, uid: string, active: boolean): Promise<{ ok: true }> {
  const action = active ? 'activate' : 'deactivate';
  return api(`/api/admin/accounts/${id}/staff-users/${uid}/${action}`, { method: 'POST' });
}
